/**
 * Draft activations in flight, per project, and which projects have a
 * switcher mounted. Module scope on purpose: switching projects remounts the
 * project layout, and a pending activation must stay locked for its own
 * project (and only that project) across the remount.
 */
const pendingByProject = new Map<string, string>();
const mountedCount = new Map<string, number>();
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

export const draftActivationRegistry = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  isPending(projectId: string): boolean {
    return pendingByProject.has(projectId);
  },

  /** Returns false when the project already has an activation in flight. */
  start(projectId: string, documentId: string): boolean {
    if (pendingByProject.has(projectId)) return false;
    pendingByProject.set(projectId, documentId);
    notify();
    return true;
  },

  finish(projectId: string): void {
    if (pendingByProject.delete(projectId)) notify();
  },

  /** Marks a switcher for the project as mounted; call the result on unmount. */
  mount(projectId: string): () => void {
    mountedCount.set(projectId, (mountedCount.get(projectId) ?? 0) + 1);
    return () => {
      const next = (mountedCount.get(projectId) ?? 1) - 1;
      if (next <= 0) mountedCount.delete(projectId);
      else mountedCount.set(projectId, next);
    };
  },

  /** True while the project's switcher is on screen, i.e. it is the open project. */
  isMounted(projectId: string): boolean {
    return (mountedCount.get(projectId) ?? 0) > 0;
  },
};
